import React from 'react';
import { Grid } from '@material-ui/core';
import { LabeledCheckbox } from './ui/labeled-checkbox';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery } from '../api/HealthSystemApi';
import { Slider } from './ui/slider';

type FilterKey = 'weight' | 'baconCutlet' | 'fat' | 'leanMeat' | 'belly' | 'ham' | 'spine' | 'shoulder' | 'money';

interface IProps {
  initialValues: HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery;
  disabled?: boolean;
  onChange: (option: HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery) => void;
  value?: HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery;
}

const DEFAULT_RANGES: { [key: string]: { min: number; max: number } } = {
  weight: { min: 0, max: 250 },
  baconCutlet: { min: 0, max: 500 },
  fat: { min: 0, max: 500 },
  leanMeat: { min: 0, max: 100 },
  belly: { min: 0, max: 50 },
  ham: { min: 0, max: 50 },
  spine: { min: 0, max: 50 },
  shoulder: { min: 0, max: 50 },
  money: { min: 0, max: 1500 }
};

const SlaughterhouseFilter = (props: IProps) => {
  const { initialValues, disabled, onChange, value } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [enabledFilters, setEnabledFilters] = React.useState<Record<FilterKey, boolean>>({
    weight: false,
    baconCutlet: false,
    fat: false,
    leanMeat: false,
    belly: false,
    ham: false,
    spine: false,
    shoulder: false,
    money: false
  });

  const [ranges, setRanges] = React.useState<Record<FilterKey, number[]>>({
    weight: initialValues.weight || [DEFAULT_RANGES.weight.min, DEFAULT_RANGES.weight.max],
    baconCutlet: initialValues.weight || [DEFAULT_RANGES.baconCutlet.min, DEFAULT_RANGES.baconCutlet.max],
    fat: initialValues.weight || [DEFAULT_RANGES.fat.min, DEFAULT_RANGES.fat.max],
    leanMeat: initialValues.leanMeat || [DEFAULT_RANGES.leanMeat.min, DEFAULT_RANGES.leanMeat.max],
    belly: initialValues.weight || [DEFAULT_RANGES.belly.min, DEFAULT_RANGES.belly.max],
    ham: initialValues.ham || [DEFAULT_RANGES.ham.min, DEFAULT_RANGES.ham.max],
    spine: initialValues.spine || [DEFAULT_RANGES.spine.min, DEFAULT_RANGES.spine.max],
    shoulder: initialValues.shoulder || [DEFAULT_RANGES.shoulder.min, DEFAULT_RANGES.shoulder.max],
    money: initialValues.money || [DEFAULT_RANGES.money.min, DEFAULT_RANGES.money.max]
  });

  // Sync when `value` changes
  React.useEffect(() => {
    if (!value) {
      return;
    }

    setRanges((prev) => ({
      weight: value.weight?.length === 2 ? value.weight : prev.weight,
      baconCutlet: value.baconCutlet?.length === 2 ? value.baconCutlet : prev.baconCutlet,
      fat: value.fat?.length === 2 ? value.fat : prev.fat,
      leanMeat: value.leanMeat?.length === 2 ? value.leanMeat : prev.leanMeat,
      belly: value.belly?.length === 2 ? value.belly : prev.belly,
      ham: value.ham?.length === 2 ? value.ham : prev.ham,
      spine: value.spine?.length === 2 ? value.spine : prev.spine,
      shoulder: value.shoulder?.length === 2 ? value.shoulder : prev.shoulder,
      money: value.money?.length === 2 ? value.money : prev.money
    }));

    setEnabledFilters({
      weight: !!value.weight?.length,
      baconCutlet: !!value.baconCutlet?.length,
      fat: !!value.fat?.length,
      leanMeat: !!value.leanMeat?.length,
      belly: !!value.belly?.length,
      ham: !!value.ham?.length,
      spine: !!value.spine?.length,
      shoulder: !!value.shoulder?.length,
      money: !!value.money?.length
    });
  }, [value]);

  // onChange is called from the event handler (never inside a setState updater,
  // which would update the parent during render).
  const handleCheckboxChange = (key: keyof typeof enabledFilters) => {
    const updated = { ...enabledFilters, [key]: !enabledFilters[key] };
    setEnabledFilters(updated);
    handleChange(ranges, updated);
  };

  const handleRangeChange = (key: keyof typeof ranges, newValue: number[]) => {
    const updated = { ...ranges, [key]: newValue };
    setRanges(updated);
    handleChange(updated, enabledFilters);
  };

  const handleChange = (customRanges = ranges, customEnabled = enabledFilters) => {
    const command: HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery = {
      weight: customEnabled.weight ? customRanges.weight : null,
      baconCutlet: customEnabled.baconCutlet ? customRanges.baconCutlet : null,
      fat: customEnabled.fat ? customRanges.fat : null,
      leanMeat: customEnabled.leanMeat ? customRanges.leanMeat : null,
      belly: customEnabled.belly ? customRanges.belly : null,
      ham: customEnabled.ham ? customRanges.ham : null,
      spine: customEnabled.spine ? customRanges.spine : null,
      shoulder: customEnabled.shoulder ? customRanges.shoulder : null,
      money: customEnabled.money ? customRanges.money : null
    };

    onChange(command);
  };

  const renderFilterControl = (labelKey: string, key: FilterKey) => (
    <Grid item xs={12} sm={4} key={key}>
      <div style={{ minHeight: 40 }}>
        <LabeledCheckbox
          checked={enabledFilters[key]}
          onCheckedChange={() => handleCheckboxChange(key)}
          label={t(`f-slaughterhouse-${labelKey}-range`)}
          disabled={disabled}
        />
      </div>
      <div className={classes.sliderControl}>
        <Slider
          value={ranges[key]}
          disabled={disabled || !enabledFilters[key]}
          onValueChange={(newValue) => handleRangeChange(key, newValue)}
          min={DEFAULT_RANGES[key].min}
          max={DEFAULT_RANGES[key].max}
          showValueLabel
          aria-label={t(`f-slaughterhouse-${labelKey}-range`)}
        />
      </div>
    </Grid>
  );

  return (
    <Grid container direction="row" className={classes.slidersRow} spacing={2}>
      {(Object.keys(DEFAULT_RANGES) as FilterKey[]).map((key) => renderFilterControl(key, key))}
    </Grid>
  );
};

export default SlaughterhouseFilter;

const useStyles = makeStyles(() =>
  createStyles({
    sliderControl: {
      width: '100%',
      padding: '0px 8px 4px'
    },
    slidersRow: {
      justifyContent: 'space-between'
    }
  })
);
